import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Eye, Download, FileText, IndianRupee, X } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const SERVICES = ['Income Tax Filing', 'GST Returns Filing', 'GST Registration', 'Professional Tax Filing', 'Company Registration', 'MSME Registration', 'Tax Audit', 'Business Advisory', 'TDS Filing', 'Other'];

export default function InvoicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [form, setForm] = useState({
    client_id: '', client_name: '', client_address: '', client_gst: '',
    invoice_date: new Date().toISOString().split('T')[0], due_date: '',
    line_items: [{ description: '', qty: 1, rate: 0 }],
    tax_type: 'CGST_SGST', gst_rate: 18,
    payment_status: 'Unpaid', payment_mode: '', payment_date: '',
    notes: '', bank_details: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchText) params.set('search', searchText);
      if (monthFilter) params.set('month', monthFilter);
      if (yearFilter) params.set('year', yearFilter);
      const [invRes, clRes] = await Promise.all([
        api.get(`/invoices?${params}`),
        api.get('/clients?limit=500')
      ]);
      setInvoices(invRes.data);
      setClients(clRes.data.clients || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [statusFilter, searchText, monthFilter, yearFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const calcTotals = (items, taxType, gstRate) => {
    const subtotal = items.reduce((s, i) => s + (i.qty || 0) * (i.rate || 0), 0);
    const taxAmt = subtotal * gstRate / 100;
    return { subtotal, taxAmt, total: subtotal + taxAmt };
  };

  const openNew = () => {
    setEditId(null);
    setForm({
      client_id: '', client_name: '', client_address: '', client_gst: '',
      invoice_date: new Date().toISOString().split('T')[0], due_date: '',
      line_items: [{ description: '', qty: 1, rate: 0 }],
      tax_type: 'CGST_SGST', gst_rate: 18,
      payment_status: 'Unpaid', payment_mode: '', payment_date: '',
      notes: '', bank_details: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (inv) => {
    setEditId(inv._id);
    setForm({
      client_id: inv.client_id, client_name: inv.client_name,
      client_address: inv.client_address || '', client_gst: inv.client_gst || '',
      invoice_date: inv.invoice_date, due_date: inv.due_date || '',
      line_items: inv.line_items?.length ? inv.line_items : [{ description: '', qty: 1, rate: 0 }],
      tax_type: inv.tax_type || 'CGST_SGST', gst_rate: inv.gst_rate !== undefined ? inv.gst_rate : 18,
      payment_status: inv.payment_status, payment_mode: inv.payment_mode || '',
      payment_date: inv.payment_date || '', notes: inv.notes || '', bank_details: inv.bank_details || ''
    });
    setDialogOpen(true);
  };

  const openView = async (inv) => {
    try {
      const { data } = await api.get(`/invoices/${inv._id}`);
      setViewInvoice(data);
      setViewDialog(true);
    } catch { toast.error('Failed to load invoice'); }
  };

  const handleClientSelect = (cid) => {
    const c = clients.find(cl => cl._id === cid);
    if (c) setForm(prev => ({ ...prev, client_id: cid, client_name: c.full_name, client_address: `${c.address || ''} ${c.pincode || ''}`.trim(), client_gst: c.gst_number || '' }));
  };

  const addLineItem = () => setForm(prev => ({ ...prev, line_items: [...prev.line_items, { description: '', qty: 1, rate: 0 }] }));
  const removeLineItem = (idx) => setForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  const updateLineItem = (idx, field, value) => {
    setForm(prev => ({ ...prev, line_items: prev.line_items.map((item, i) => i === idx ? { ...item, [field]: field === 'qty' || field === 'rate' ? Number(value) : value } : item) }));
  };

  const handleSubmit = async () => {
    if (!form.client_name) { toast.error('Client is required'); return; }
    if (!form.line_items.some(i => i.description)) { toast.error('At least one line item required'); return; }
    try {
      if (editId) { await api.put(`/invoices/${editId}`, form); toast.success('Invoice updated'); }
      else { await api.post('/invoices', form); toast.success('Invoice created'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete invoice?')) return;
    try { await api.delete(`/invoices/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const downloadPdf = async (inv) => {
    try {
      const response = await api.get(`/invoices/${inv._id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `Invoice_${inv.invoice_number?.replace(/\//g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF download failed'); }
  };

  const { subtotal, taxAmt, total } = calcTotals(form.line_items, form.tax_type, form.gst_rate);
  const totalRevenue = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.payment_status === 'Paid').reduce((s, i) => s + (i.total || 0), 0);
  const totalUnpaid = totalRevenue - totalPaid;

  return (
    <div data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Invoices</h1>
          <p className="text-sm text-[#4F734F] mt-1">Create and manage client invoices</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/exports/invoices"
            params={{ ...(searchText ? { search: searchText } : {}), ...(statusFilter ? { status: statusFilter } : {}), ...(monthFilter ? { month: monthFilter } : {}), ...(yearFilter ? { year: yearFilter } : {}) }}
            filename="invoices"
            testIdPrefix="inv"
          />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="create-invoice-btn"><Plus size={16} /> New Invoice</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total Billed</p><p className="font-heading text-2xl font-light text-[#111B11]">{totalRevenue.toLocaleString('en-IN')}</p></div>
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Paid</p><p className="font-heading text-2xl font-light text-green-600">{totalPaid.toLocaleString('en-IN')}</p></div>
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Outstanding</p><p className="font-heading text-2xl font-light text-red-600">{totalUnpaid.toLocaleString('en-IN')}</p></div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search client, invoice no..."
          testId="inv-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={monthFilter || 'all'} onValueChange={(v) => setMonthFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-11 input-field"><SelectValue placeholder="All Months" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Months</SelectItem>{['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => <SelectItem key={m} value={m}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={yearFilter || 'all'} onValueChange={(v) => setYearFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-28 h-11 input-field"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{['2024','2025','2026'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-11 input-field"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Unpaid">Unpaid</SelectItem><SelectItem value="Paid">Paid</SelectItem><SelectItem value="Partial">Partial</SelectItem><SelectItem value="Overdue">Overdue</SelectItem></SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr className="bg-[#1B2A1B] text-white">
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Invoice #</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Date</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Client</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Amount</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Tax</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Total</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Status</th>
              <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-28">Actions</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="text-center py-8 text-olive-400">Loading...</td></tr> :
              invoices.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-olive-400">No invoices found</td></tr> :
              invoices.map(inv => (
                <tr key={inv._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7]">
                  <td className="py-2 px-3 font-mono text-xs font-medium text-[#D4AF37]">{inv.invoice_number}</td>
                  <td className="py-2 px-3">{inv.invoice_date}</td>
                  <td className="py-2 px-3 font-medium text-[#111B11]">{inv.client_name}</td>
                  <td className="py-2 px-3 text-right font-mono">{(inv.subtotal || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3 text-right font-mono text-olive-500">{(inv.tax_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3 text-right font-mono font-medium">{(inv.total || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${inv.payment_status === 'Paid' ? 'bg-green-100 text-green-700' : inv.payment_status === 'Partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{inv.payment_status}</span></td>
                  <td className="py-2 px-3">
                    <div className="flex gap-0.5">
                      <button onClick={() => openView(inv)} className="p-1 hover:bg-olive-100 rounded-sm" data-testid={`view-inv-${inv._id}`}><Eye size={13} className="text-olive-500" /></button>
                      <button onClick={() => downloadPdf(inv)} className="p-1 hover:bg-olive-100 rounded-sm" data-testid={`pdf-inv-${inv._id}`}><Download size={13} className="text-[#D4AF37]" /></button>
                      <button onClick={() => openEdit(inv)} className="p-1 hover:bg-olive-100 rounded-sm"><Pencil size={13} className="text-olive-500" /></button>
                      {user?.role === 'admin' && <button onClick={() => handleDelete(inv._id)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client</Label>
                <Select value={form.client_id || 'none'} onValueChange={handleClientSelect}>
                  <SelectTrigger className="input-field"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Client GST</Label><Input value={form.client_gst} onChange={(e) => setForm(prev => ({ ...prev, client_gst: e.target.value }))} className="input-field font-mono" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm(prev => ({ ...prev, invoice_date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Tax Type</Label>
                <Select value={form.tax_type} onValueChange={(v) => setForm(prev => ({ ...prev, tax_type: v, gst_rate: v === 'EXEMPT' ? 0 : prev.gst_rate || 18 }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CGST_SGST">CGST + SGST</SelectItem><SelectItem value="IGST">IGST</SelectItem><SelectItem value="EXEMPT">Exempt (0%)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.tax_type !== 'EXEMPT' && (
              <div className="space-y-1.5 max-w-[200px]">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">GST Rate</Label>
                <Select value={String(form.gst_rate)} onValueChange={(v) => setForm(prev => ({ ...prev, gst_rate: Number(v) }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="5">5%</SelectItem><SelectItem value="12">12%</SelectItem><SelectItem value="18">18%</SelectItem></SelectContent>
                </Select>
              </div>
            )}

            {/* Line Items */}
            <div className="border border-[#E6EDE6] rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase font-semibold text-[#4F734F]">Line Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="gap-1 text-xs" data-testid="add-line-item"><Plus size={12} /> Add Item</Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <Select value={item.description || 'custom'} onValueChange={(v) => { if (v !== 'custom') updateLineItem(idx, 'description', v); }}>
                        <SelectTrigger className="input-field text-xs h-9"><SelectValue placeholder="Service" /></SelectTrigger>
                        <SelectContent>{SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}<SelectItem value="custom">Custom...</SelectItem></SelectContent>
                      </Select>
                      {(!SERVICES.includes(item.description)) && <Input value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} className="input-field text-xs h-8 mt-1" placeholder="Description" />}
                    </div>
                    <div className="col-span-2"><Input type="number" value={item.qty} onChange={(e) => updateLineItem(idx, 'qty', e.target.value)} className="input-field text-xs h-9 text-center" min="1" /></div>
                    <div className="col-span-3"><Input type="number" value={item.rate} onChange={(e) => updateLineItem(idx, 'rate', e.target.value)} className="input-field text-xs h-9 text-right font-mono" /></div>
                    <div className="col-span-1 text-right font-mono text-xs text-[#111B11]">{((item.qty || 0) * (item.rate || 0)).toLocaleString('en-IN')}</div>
                    <div className="col-span-1 text-right">{form.line_items.length > 1 && <button onClick={() => removeLineItem(idx)} className="p-1 hover:bg-red-50 rounded-sm"><X size={12} className="text-red-400" /></button>}</div>
                  </div>
                ))}
              </div>
              {/* Totals */}
              <div className="mt-4 pt-3 border-t border-[#E6EDE6] space-y-1 text-sm">
                <div className="flex justify-end gap-8"><span className="text-olive-500">Subtotal:</span><span className="font-mono w-24 text-right">{subtotal.toLocaleString('en-IN')}</span></div>
                {form.tax_type === 'EXEMPT' ? (
                  <div className="flex justify-end gap-8"><span className="text-olive-500">Tax:</span><span className="font-mono w-24 text-right">Exempt</span></div>
                ) : form.tax_type === 'CGST_SGST' ? (<>
                  <div className="flex justify-end gap-8"><span className="text-olive-500">CGST ({form.gst_rate/2}%):</span><span className="font-mono w-24 text-right">{(subtotal * form.gst_rate / 200).toFixed(2)}</span></div>
                  <div className="flex justify-end gap-8"><span className="text-olive-500">SGST ({form.gst_rate/2}%):</span><span className="font-mono w-24 text-right">{(subtotal * form.gst_rate / 200).toFixed(2)}</span></div>
                </>) : (
                  <div className="flex justify-end gap-8"><span className="text-olive-500">IGST ({form.gst_rate}%):</span><span className="font-mono w-24 text-right">{taxAmt.toFixed(2)}</span></div>
                )}
                <div className="flex justify-end gap-8 font-semibold text-base pt-1 border-t border-[#E6EDE6]"><span className="text-[#111B11]">Total:</span><span className="font-mono w-24 text-right text-[#111B11]">INR {total.toLocaleString('en-IN')}</span></div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={(v) => setForm(prev => ({ ...prev, payment_status: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unpaid">Unpaid</SelectItem><SelectItem value="Paid">Paid</SelectItem><SelectItem value="Partial">Partial</SelectItem><SelectItem value="Overdue">Overdue</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Mode</Label>
                <Select value={form.payment_mode || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, payment_mode: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="input-field"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem><SelectItem value="Cash">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem><SelectItem value="Cheque">Cheque</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Date</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm(prev => ({ ...prev, payment_date: e.target.value }))} className="input-field" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Bank Details (for invoice)</Label><Textarea value={form.bank_details} onChange={(e) => setForm(prev => ({ ...prev, bank_details: e.target.value }))} className="input-field" placeholder="A/C No, IFSC, Bank Name..." /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" /></div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-invoice-btn">{editId ? 'Update' : 'Create'} Invoice</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewInvoice && (<>
            <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><FileText size={20} className="text-[#D4AF37]" /> Invoice {viewInvoice.invoice_number}</DialogTitle></DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-olive-500">Date:</span> <span className="font-medium">{viewInvoice.invoice_date}</span></div>
                <div><span className="text-olive-500">Status:</span> <span className={`font-medium ${viewInvoice.payment_status === 'Paid' ? 'text-green-700' : 'text-red-600'}`}>{viewInvoice.payment_status}</span></div>
                <div><span className="text-olive-500">Client:</span> <span className="font-medium">{viewInvoice.client_name}</span></div>
                {viewInvoice.client_gst && <div><span className="text-olive-500">Client GST:</span> <span className="font-mono text-xs">{viewInvoice.client_gst}</span></div>}
              </div>
              <table className="w-full text-sm border border-[#E6EDE6]">
                <thead><tr className="bg-[#F4F7F4]"><th className="py-2 px-3 text-left text-xs">#</th><th className="py-2 px-3 text-left text-xs">Description</th><th className="py-2 px-3 text-center text-xs">Qty</th><th className="py-2 px-3 text-right text-xs">Rate</th><th className="py-2 px-3 text-right text-xs">Amount</th></tr></thead>
                <tbody>{(viewInvoice.line_items || []).map((item, i) => (<tr key={i} className="border-t border-[#E6EDE6]"><td className="py-2 px-3">{i+1}</td><td className="py-2 px-3">{item.description}</td><td className="py-2 px-3 text-center">{item.qty}</td><td className="py-2 px-3 text-right font-mono">{item.rate?.toLocaleString('en-IN')}</td><td className="py-2 px-3 text-right font-mono">{(item.qty * item.rate).toLocaleString('en-IN')}</td></tr>))}</tbody>
              </table>
              <div className="text-sm space-y-1 text-right">
                <div><span className="text-olive-500 mr-4">Subtotal:</span><span className="font-mono">{viewInvoice.subtotal?.toLocaleString('en-IN')}</span></div>
                {viewInvoice.tax_type === 'EXEMPT' ? <div><span className="text-olive-500 mr-4">Tax:</span><span className="font-mono">Exempt</span></div> : viewInvoice.tax_type === 'IGST' ? <div><span className="text-olive-500 mr-4">IGST ({viewInvoice.gst_rate}%):</span><span className="font-mono">{viewInvoice.igst?.toLocaleString('en-IN')}</span></div> : <><div><span className="text-olive-500 mr-4">CGST ({viewInvoice.gst_rate/2}%):</span><span className="font-mono">{viewInvoice.cgst?.toLocaleString('en-IN')}</span></div><div><span className="text-olive-500 mr-4">SGST ({viewInvoice.gst_rate/2}%):</span><span className="font-mono">{viewInvoice.sgst?.toLocaleString('en-IN')}</span></div></>}
                <div className="font-semibold text-base pt-1 border-t border-[#E6EDE6]"><span className="mr-4">Total:</span><span className="font-mono">INR {viewInvoice.total?.toLocaleString('en-IN')}</span></div>
              </div>
              <Button onClick={() => downloadPdf(viewInvoice)} className="btn-accent gap-2 w-full" data-testid="download-pdf-btn"><Download size={16} /> Download PDF</Button>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
